/**
 * Mini-Cart-UI: Button (Icon + Counter) + Slide-Out Drawer mit Items + Checkout.
 *
 * Cart-State liegt in localStorage (pro Agent). Beim Checkout wird ein
 * kurzlebiger Cart-Token im Backend erzeugt und der User auf
 * `dpconnect.de/?dp_cart=<token>` umgeleitet — WP-Snippet loest den Token
 * ein und befuellt den WC-Cart.
 */
import { useEffect, useRef, useState } from "react";
import { Lock, Minus, Plus, ShoppingCart, Trash2, X } from "lucide-react";
import { useCart } from "../lib/cart_store";
import { createCartToken } from "../lib/page";
import { useCustomer } from "../lib/customer_auth";
import { navigate } from "../lib/router";

type ThemeLike = {
  accent: string;
  ink: string;
  inkMuted: string;
  cardBg: string;
};

export function MiniCartButton({
  agentCode,
  theme,
  isDark,
}: {
  agentCode: string;
  theme: ThemeLike;
  isDark: boolean;
}) {
  const [open, setOpen] = useState(false);
  const { count } = useCart(agentCode);

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        className="relative inline-flex items-center gap-1.5 rounded-lg px-2.5 md:px-3 py-1.5 text-xs md:text-sm font-semibold border"
        style={{
          color: theme.ink,
          background: theme.cardBg,
          borderColor: isDark ? "rgba(255,255,255,.10)" : "rgba(0,0,0,.10)",
        }}
        aria-label={`Warenkorb mit ${count} Artikeln`}
      >
        <ShoppingCart className="size-4" />
        <span className="hidden sm:inline">Korb</span>
        {count > 0 && (
          <span
            className="absolute -top-1.5 -right-1.5 min-w-[18px] h-[18px] grid place-items-center rounded-full text-[10px] font-bold px-1"
            style={{ background: theme.accent, color: "#fff" }}
          >
            {count > 99 ? "99+" : count}
          </span>
        )}
      </button>
      {open && (
        <CartDrawer agentCode={agentCode} theme={theme} isDark={isDark} onClose={() => setOpen(false)} />
      )}
    </>
  );
}

function CartDrawer({
  agentCode,
  theme,
  isDark,
  onClose,
}: {
  agentCode: string;
  theme: ThemeLike;
  isDark: boolean;
  onClose: () => void;
}) {
  const { items, update, remove, clear, count } = useCart(agentCode);
  const { customer } = useCustomer();
  const [checkingOut, setCheckingOut] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const drawerRef = useRef<HTMLDivElement>(null);

  // ESC + Body-Scroll-Lock
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [onClose]);

  const subtotal = items.reduce((sum, it) => {
    const p = parseFloat(it.price || "0");
    return sum + (isNaN(p) ? 0 : p * it.qty);
  }, 0);

  const onCheckout = async () => {
    setError(null);
    setCheckingOut(true);
    // Eingeloggter Customer → direkt zum Vorkasse-Checkout auf der Subdomain
    if (customer) {
      onClose();
      navigate("/checkout");
      return;
    }
    // Nicht eingeloggt → Cross-Domain-Token zu dpconnect.de
    try {
      const resp = await createCartToken(
        agentCode,
        items.map((it) => ({
          wc_product_id: it.wc_product_id,
          qty: it.qty,
          variation_id: it.variation_id ?? null,
        })),
      );
      window.location.href = resp.redirect_url;
    } catch (e) {
      setError(e instanceof Error ? e.message : "Checkout fehlgeschlagen");
      setCheckingOut(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[60]" aria-modal="true" role="dialog">
      <style>{`
        @keyframes dp-cart-slide-in {
          from { transform: translate3d(100%, 0, 0); }
          to   { transform: translate3d(0, 0, 0); }
        }
        @keyframes dp-cart-backdrop-in {
          from { opacity: 0; }
          to   { opacity: 1; }
        }
      `}</style>
      <div
        className="absolute inset-0"
        style={{
          background: "rgba(0,0,0,0.55)",
          backdropFilter: "blur(2px)",
          WebkitBackdropFilter: "blur(2px)",
          animation: "dp-cart-backdrop-in 0.22s ease-out both",
        }}
        onClick={onClose}
      />
      <div
        ref={drawerRef}
        className="fixed inset-y-0 right-0 w-full sm:max-w-md flex flex-col shadow-2xl"
        style={{
          background: theme.cardBg,
          color: theme.ink,
          height: "100dvh",
          maxHeight: "100dvh",
          paddingTop: "env(safe-area-inset-top, 0px)",
          paddingBottom: "env(safe-area-inset-bottom, 0px)",
          animation: "dp-cart-slide-in 0.28s cubic-bezier(0.32, 0.72, 0.24, 1) both",
          willChange: "transform",
        }}
      >
        <div
          className="flex items-center justify-between px-4 py-3 border-b flex-shrink-0"
          style={{ borderColor: isDark ? "rgba(255,255,255,.08)" : "rgba(0,0,0,.08)" }}
        >
          <div className="flex items-center gap-2">
            <ShoppingCart className="size-5" style={{ color: theme.accent }} />
            <h3 className="text-base font-semibold">
              Dein Korb {count > 0 && <span style={{ color: theme.inkMuted }}>({count})</span>}
            </h3>
          </div>
          <button
            onClick={onClose}
            className="rounded-lg p-1.5 hover:bg-current/5"
            aria-label="Schließen"
          >
            <X className="size-5" />
          </button>
        </div>

        {/* Items — scrollbar mit smooth touch */}
        <div
          className="flex-1 overflow-y-auto px-2 py-2"
          style={{ WebkitOverflowScrolling: "touch", overscrollBehavior: "contain" }}
        >
          {items.length === 0 ? (
            <div className="grid place-items-center h-full text-center px-6">
              <div>
                <ShoppingCart className="size-12 mx-auto opacity-30 mb-3" />
                <p className="text-sm" style={{ color: theme.inkMuted }}>
                  Dein Korb ist leer
                </p>
                <button
                  onClick={onClose}
                  className="mt-4 inline-flex items-center text-xs font-semibold underline"
                  style={{ color: theme.accent }}
                >
                  Weiter stöbern
                </button>
              </div>
            </div>
          ) : (
            <ul className="space-y-1.5">
              {items.map((it) => (
                <li
                  key={`${it.wc_product_id}::${it.variation_id ?? ""}`}
                  className="flex gap-3 p-2 rounded-xl"
                  style={{ background: isDark ? "rgba(255,255,255,.03)" : "rgba(0,0,0,.02)" }}
                >
                  <div
                    className="size-16 rounded-lg overflow-hidden flex-shrink-0 grid place-items-center"
                    style={{ background: isDark ? "rgba(255,255,255,.06)" : "rgba(0,0,0,.04)" }}
                  >
                    {it.image_url ? (
                      <img src={it.image_url} alt="" className="w-full h-full object-cover" loading="lazy" />
                    ) : (
                      <ShoppingCart className="size-5 opacity-30" />
                    )}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium leading-snug line-clamp-2">
                      {it.name}
                    </div>
                    {it.variation_label && (
                      <div className="text-xs mt-0.5" style={{ color: theme.inkMuted }}>
                        {it.variation_label}
                      </div>
                    )}
                    <div className="mt-1.5 flex items-center justify-between gap-2">
                      <div
                        className="inline-flex items-center rounded-lg border"
                        style={{
                          borderColor: isDark ? "rgba(255,255,255,.10)" : "rgba(0,0,0,.10)",
                        }}
                      >
                        <button
                          onClick={() => update(it.wc_product_id, it.variation_id, it.qty - 1)}
                          className="size-7 grid place-items-center hover:bg-current/5"
                          aria-label="Weniger"
                        >
                          <Minus className="size-3" />
                        </button>
                        <span className="px-2 text-xs font-semibold tabular-nums min-w-[28px] text-center">
                          {it.qty}
                        </span>
                        <button
                          onClick={() => update(it.wc_product_id, it.variation_id, it.qty + 1)}
                          className="size-7 grid place-items-center hover:bg-current/5"
                          aria-label="Mehr"
                        >
                          <Plus className="size-3" />
                        </button>
                      </div>
                      {it.price ? (
                        <div className="text-sm font-bold" style={{ color: theme.accent }}>
                          {(parseFloat(it.price) * it.qty).toFixed(2).replace(".", ",")} €
                        </div>
                      ) : (
                        <div className="text-[11px] inline-flex items-center gap-1" style={{ color: theme.inkMuted }}>
                          <Lock className="size-3" />
                          Preis nach Login
                        </div>
                      )}
                    </div>
                  </div>
                  <button
                    onClick={() => remove(it.wc_product_id, it.variation_id)}
                    className="self-start text-current/40 hover:text-rose-600"
                    aria-label="Entfernen"
                  >
                    <Trash2 className="size-4" />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Footer mit Total + Checkout — bleibt unten sichtbar */}
        {items.length > 0 && (
          <div
            className="border-t px-4 py-3 space-y-3 flex-shrink-0"
            style={{
              borderColor: isDark ? "rgba(255,255,255,.08)" : "rgba(0,0,0,.08)",
              background: theme.cardBg,
            }}
          >
            {subtotal > 0 && (
              <div className="flex items-center justify-between text-sm">
                <span style={{ color: theme.inkMuted }}>Zwischensumme</span>
                <span className="font-bold tabular-nums" style={{ color: theme.ink }}>
                  {subtotal.toFixed(2).replace(".", ",")} €
                </span>
              </div>
            )}
            <p className="text-[11px]" style={{ color: theme.inkMuted }}>
              Checkout findet bei dpconnect.de statt — Login / Registrierung dort möglich.
              MwSt + Versand werden im Checkout berechnet.
            </p>
            {error && (
              <div className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-800">
                {error}
              </div>
            )}
            <div className="flex gap-2">
              <button
                onClick={() => {
                  if (window.confirm("Wirklich leeren?")) clear();
                }}
                className="text-xs underline"
                style={{ color: theme.inkMuted }}
              >
                Korb leeren
              </button>
              <button
                onClick={onCheckout}
                disabled={checkingOut}
                className="ml-auto inline-flex items-center justify-center gap-2 rounded-xl px-5 py-3 text-sm font-bold shadow-lg disabled:opacity-60"
                style={{ background: theme.accent, color: "#fff" }}
              >
                {checkingOut ? "Weiterleitung…" : "Zur Kasse →"}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
